import { useContext } from "react";
import classNames from "classnames";

import {
  useCalculateOperatingHours,
  Status,
} from "../../hooks/useCalculateOperatingHours";

import { OperatingHours } from "../../utils";
import { FloorChange } from "../../utils/getFloorDifference";
import getDistance from "../../utils/getDistance";

import { MapContext } from "../Map/Map";
import DirectionChip from "../DirectionChip/DirectionChip";

import styles from "./FeatureInformation.module.scss";
import { useTranslation } from "react-i18next";

export const wrapOpeningHours = (text: string) =>
  text.replace(/(Open|Closed)/g, "<span>$&</span>");

interface Props {
  dataQA: string;
  name: string;
  operatingHours: OperatingHours;
  category?: string | null;
  building?: string;
  floorName?: string;
  className?: string;
  isTemporarilyClosed: boolean;
  floorChange: { direction: FloorChange; amount: number };
  kilometres?: number;
  imageSrc?: string;
  fromCarousel?: boolean;
}

export default function FeatureInformation({
  dataQA,
  name,
  operatingHours,
  category,
  building,
  floorName,
  className,
  isTemporarilyClosed,
  floorChange,
  kilometres,
  imageSrc,
  fromCarousel,
}: Props) {
  const { t } = useTranslation();

  const { controlTheme, distanceUnit = "metric" } = useContext(MapContext);

  const { statusCopy, status } = useCalculateOperatingHours(operatingHours);

  const openingHoursWrappedString = statusCopy
    ? wrapOpeningHours(statusCopy)
    : null;

  const distance = kilometres ? getDistance(kilometres, distanceUnit) : null;

  const nearestLabelLength = t("sheet_wrapper.nearest_label_text").length;

  return (
    <div
      className={classNames(styles.container, styles[controlTheme], className, {
        [styles.showImage]: imageSrc,
      })}
      data-qa={dataQA}
    >
      <div className={styles.content}>
        <div
          className={styles.info}
          {...(fromCarousel && {
            style: {
              maxWidth:
                nearestLabelLength === 7
                  ? "20rem"
                  : 20 - nearestLabelLength / 4 + "rem",
            },
          })}
        >
          <p className={styles.name}>{name}</p>
          {(category || floorName || building || operatingHours) && (
            <div className={styles.description}>
              {category && <p className={styles.subtitle}>{category}</p>}
              {(floorName || building) && (
                <p className={styles.address}>
                  {floorName && floorName}
                  {floorName && building && (
                    <span className={styles.separator}>·</span>
                  )}
                  {building && building}
                </p>
              )}
              {operatingHours && (
                <p
                  className={classNames(styles.openingTimes, {
                    [styles.closed]: status === Status.CLOSED,
                    [styles.tempClosed]: isTemporarilyClosed,
                  })}
                  dangerouslySetInnerHTML={{
                    __html: isTemporarilyClosed
                      ? Status.TEMP_CLOSED
                      : openingHoursWrappedString || "",
                  }}
                />
              )}
            </div>
          )}
        </div>
        <DirectionChip
          dataQA="direction-chip"
          appearance="plain"
          className={styles.directionChip}
          floorChange={floorChange}
          distance={distance}
          textOverImage={Boolean(imageSrc)}
        />
      </div>
    </div>
  );
}
